import { camelize } from 'casing'
import type { Product, ProductsV2State } from '../types'

export const DEFAULT_PRODUCTS_V2_PAGE_SIZE = 30

export type ProductsV2List = {
  rows: Product[]
  pageNumber: number
  pageSize: number
  totalCount?: number
  hasNextPage: boolean
}

type ProductsV2Response = {
  rows?: Product[]
  products?: {
    rows?: Product[]
    pageNumber?: number
    pageSize?: number
    totalCount?: number
    count?: number
    hasNextPage?: boolean
    links?: { nextPage?: string }
  }
  pageNumber?: number
  pageSize?: number
  totalCount?: number
  count?: number
  hasNextPage?: boolean
  links?: { nextPage?: string }
}

const toOptionalCount = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  return undefined
}

const toOptionalBoolean = (value: unknown): boolean | undefined => {
  return typeof value === 'boolean' ? value : undefined
}

export const toProductsV2List = (
  data: unknown,
  page: { pageNumber: number; pageSize: number }
): ProductsV2List => {
  const camelized = camelize(data) as ProductsV2Response
  const rows = Array.isArray(camelized.products?.rows)
    ? camelized.products.rows
    : Array.isArray(camelized.rows)
      ? camelized.rows
      : []
  const pageNumber = page.pageNumber
  const pageSize = page.pageSize
  const totalCount = toOptionalCount(
    camelized.products?.totalCount ?? camelized.totalCount
  )
  const explicitHasNextPage = toOptionalBoolean(
    camelized.products?.hasNextPage ?? camelized.hasNextPage
  )
  const nextPage =
    camelized.products?.links?.nextPage || camelized.links?.nextPage
  const loadedCount = (pageNumber - 1) * pageSize + rows.length
  const hasNextPage =
    explicitHasNextPage ??
    (Boolean(nextPage) ||
      (totalCount != null ? loadedCount < totalCount : false))

  return {
    rows,
    pageNumber,
    pageSize,
    totalCount,
    hasNextPage,
  }
}

export const mergeProductsV2Rows = (
  existing: Product[],
  incoming: Product[],
  pageNumber: number
): Product[] => {
  if (pageNumber <= 1) {
    return incoming
  }

  const seen = new Set(existing.map(product => product.productId))
  return [
    ...existing,
    ...incoming.filter(product => !seen.has(product.productId)),
  ]
}

export const nextProductsV2State = (
  existing: ProductsV2State,
  incoming: {
    rows?: Product[]
    pageNumber?: number
    pageSize?: number
    totalCount?: number
    hasNextPage?: boolean
  }
): ProductsV2State => {
  const pageNumber = incoming.pageNumber ?? 1
  const pageSize = incoming.pageSize ?? existing.pageSize
  const incomingRows = incoming.rows ?? []
  const rows = mergeProductsV2Rows(existing.rows, incomingRows, pageNumber)
  const totalCount =
    incoming.totalCount ??
    (pageNumber <= 1 ? rows.length : existing.totalCount)
  const hasMore =
    incoming.hasNextPage === true ||
    (incoming.hasNextPage !== false && rows.length < totalCount)

  return {
    rows,
    pageNumber,
    pageSize,
    totalCount,
    hasMore,
  }
}
